import { useState, type FormEvent } from 'react'
import { Check, Plus, RotateCcw } from 'lucide-react'
import type { ShiftNote, ShiftNoteCategory } from '@/api/shiftDashboardApi'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useShiftNoteMutations, useShiftNotes } from '@/hooks/useShiftDashboard'
import { NOTE_CATEGORIES, NOTE_MAX_LENGTH, noteCategoryLabel, sortNotes } from '@/lib/shiftNotes'
import { cn } from '@/lib/utils'

/**
 * Shift notes (z8nrz7fbcq): free text for what Smart-CGT does not capture —
 * a safety issue, a lab message, a borrowed instrument. Kept per day and
 * shift, read by the next shift at handover. A note's text is never edited:
 * it is resolved (and can be reopened) instead, and both are audited.
 */
export function ShiftNotesWidget({
  date,
  shiftDefinitionId,
  siteId,
  teamId,
  canWrite,
}: {
  date: string
  shiftDefinitionId: string | null
  siteId: string | null
  teamId: string | null
  canWrite: boolean
}) {
  const notes = useShiftNotes({ date, shiftDefinitionId, siteId, teamId })
  const { create, setResolved } = useShiftNoteMutations()
  const [composing, setComposing] = useState(false)
  const [text, setText] = useState('')
  const [category, setCategory] = useState<ShiftNoteCategory>('general')
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setComposing(false)
    setText('')
    setCategory('general')
    setError(null)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = text.trim()
    if (!trimmed) return
    setError(null)
    create.mutate(
      { date, shiftDefinitionId, siteId, teamId, category, text: trimmed },
      { onSuccess: close, onError: (err) => setError(err.message) },
    )
  }

  const list = sortNotes(notes.data ?? [])
  const open = list.filter((n) => !n.resolved).length

  return (
    <section aria-labelledby='shift-notes-heading' className='rounded-xl border border-gray-200 bg-white shadow-sm'>
      <header className='flex items-baseline justify-between gap-3 border-b px-4 py-3'>
        <h2 id='shift-notes-heading' className='text-sm font-semibold text-foreground'>
          Shift notes
        </h2>
        {list.length > 0 && <span className='text-xs text-muted-foreground'>{open} open</span>}
      </header>

      <div className='p-4'>
        {canWrite &&
          (composing ? (
            <form onSubmit={submit} className='mb-4 space-y-2'>
              <div role='radiogroup' aria-label='Note type' className='flex flex-wrap gap-1.5'>
                {NOTE_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type='button'
                    role='radio'
                    aria-checked={category === c.id}
                    onClick={() => setCategory(c.id)}
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                      category === c.id
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50',
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={NOTE_MAX_LENGTH}
                rows={3}
                autoFocus
                placeholder='What should this shift know? For example: fire door in corridor B propped open until 14:00.'
                aria-label='Note'
              />
              {error && <p className='text-xs text-destructive'>{error}</p>}
              <div className='flex justify-end gap-2'>
                <Button type='button' size='sm' variant='ghost' onClick={close}>
                  Cancel
                </Button>
                <Button type='submit' size='sm' disabled={!text.trim() || create.isPending}>
                  Add note
                </Button>
              </div>
            </form>
          ) : (
            <button
              type='button'
              onClick={() => setComposing(true)}
              className='mb-4 flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-left text-sm text-muted-foreground hover:border-foreground/40 hover:text-foreground'
            >
              <Plus className='h-4 w-4' /> Add a note for this shift
            </button>
          ))}

        {notes.error ? (
          <p className='text-xs text-destructive'>{notes.error.message}</p>
        ) : notes.isLoading ? (
          <div className='h-12 animate-pulse rounded-lg bg-muted motion-reduce:animate-none' />
        ) : list.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No notes yet. Use them for safety issues, lab messages or anything the system does not record.
          </p>
        ) : (
          <ul className='max-h-80 space-y-3 overflow-y-auto overscroll-contain pr-1'>
            {list.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                canWrite={canWrite}
                busy={setResolved.isPending}
                onToggle={() => setResolved.mutate({ id: note.id, resolved: !note.resolved })}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function NoteItem({
  note,
  canWrite,
  busy,
  onToggle,
}: {
  note: ShiftNote
  canWrite: boolean
  busy: boolean
  onToggle: () => void
}) {
  const hazard = note.category === 'safety' && !note.resolved
  return (
    <li className={cn('border-l-2 pl-3', hazard ? 'border-l-red-600' : 'border-l-border', note.resolved && 'opacity-70')}>
      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
        <span className={cn('font-semibold', hazard ? 'text-red-700' : 'text-foreground')}>{noteCategoryLabel(note.category)}</span>
        <span>
          {note.createdBy}, {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
        {canWrite && (
          <button
            type='button'
            onClick={onToggle}
            disabled={busy}
            className='ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium hover:bg-muted hover:text-foreground'
          >
            {note.resolved ? <RotateCcw className='h-3 w-3' /> : <Check className='h-3 w-3' />}
            {note.resolved ? 'Reopen' : 'Resolve'}
          </button>
        )}
      </div>
      <p className={cn('mt-1 whitespace-pre-wrap text-sm text-foreground', note.resolved && 'line-through decoration-muted-foreground/60')}>
        {note.text}
      </p>
      {note.resolved && note.resolvedBy && <p className='mt-0.5 text-xs text-muted-foreground'>Resolved by {note.resolvedBy}</p>}
    </li>
  )
}
